import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContextBackend';
import { isValidRedirect } from '../utils/redirect';
import { isMainDomain, buildSubdomainUrl, getBaseDomain } from '../utils/subdomain';
import { setAccessToken as setStoredAccessToken, setRefreshToken as setStoredRefreshToken } from '../utils/tokenStorage';

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const { setAuthData } = useAuth();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent multiple executions (React StrictMode double-invokes effects)
      if (hasProcessedRef.current) {
        console.log('⚠️ OAuth callback already processed, skipping...');
        return;
      }
      hasProcessedRef.current = true;
      console.log('🔄 Processing OAuth callback...');

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      // Check if tokens are passed directly in URL (backend redirect approach)
      const success = searchParams.get('success');
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const expiresIn = searchParams.get('expires_in');

      console.log(accessToken, refreshToken, expiresIn, searchParams);

      // Handle OAuth error from Keycloak or backend
      if (errorParam) {
        const errorDescription = searchParams.get('error_description');
        console.error('❌ OAuth error:', errorParam, errorDescription);

        // Handle specific errors
        if (errorParam === 'account_not_registered') {
          setError('Account not registered. Please sign up first.');
          setIsProcessing(false);
          setTimeout(() => navigate('/register'), 3000);
          return;
        }
        if (errorParam === 'account_already_exists') {
          setError('Account already exists. Please login instead.');
          setIsProcessing(false);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        setError(`Authentication failed: ${errorDescription || errorParam}`);
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Handle tokens passed directly in URL (backend already processed everything)
      if (success === 'true' && accessToken && refreshToken) {
        console.log('✅ Tokens received directly from backend redirect');

        try {
          // Decode JWT to extract user info
          const base64Url = accessToken.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join(''));

          const tokenData = JSON.parse(jsonPayload);
          console.log('📋 Token data:', tokenData);
          console.log('🔍 Raw user_uuid from token:', tokenData.user_uuid, 'Type:', typeof tokenData.user_uuid);

          // CRITICAL: Ensure user_uuid is always a string to prevent precision loss
          // JWT parsing may convert large numbers, losing precision
          const userUuid = String(tokenData.user_uuid || tokenData.sub);
          console.log('✅ Converted user_uuid to string:', userUuid);

          // Extract user data from token
          const userData = {
            user_uuid: userUuid, // Already converted to string
            email: tokenData.email,
            first_name: tokenData.given_name,
            last_name: tokenData.family_name,
            sub: tokenData.sub,
          };

          // Extract account data from token (if available)
          const accountData = {
            account_uuid: tokenData.account_id ? parseInt(tokenData.account_id) : null,
            account_name: tokenData.account_name || '',
            domain: tokenData.email ? tokenData.email.split('@')[1] : '',
          };

          // Store tokens first so we can make API calls
          setStoredAccessToken(accessToken);
          setStoredRefreshToken(refreshToken);

          // Fetch full user data from /me API to get user_category, is_customer_admin, etc.
          const baseUrl = import.meta.env.VITE_PRIVATE_API_BASE_URL || 'http://localhost:8000/core/api/v1';
          const meResponse = await fetch(`${baseUrl}/me`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json',
            },
          });

          let fullUserData = userData;
          let fullAccountData = accountData;

          if (meResponse.ok) {
            const meData = await meResponse.json();
            console.log('📋 Full user data from /me API:', meData);
            fullUserData = meData.user || userData;
            fullAccountData = meData.account || accountData;
          } else {
            console.warn('⚠️ Could not fetch /me data, using JWT data');
          }

          localStorage.setItem('user', JSON.stringify(fullUserData));
          localStorage.setItem('account', JSON.stringify(fullAccountData));

          // Update auth context with full data
          setAuthData({
            success: true,
            access_token: accessToken,
            refresh_token: refreshToken,
            expires_in: expiresIn ? parseInt(expiresIn) : 1200,
            user: fullUserData,
            account: fullAccountData,
          });

          // Clear OAuth state
          sessionStorage.removeItem('oauth_state');
          sessionStorage.removeItem('oauth_flow');
          sessionStorage.removeItem('oauth_provider');

          console.log('✅ Authentication successful!');

          // Redirect to intended path or dashboard, with subdomain awareness
          const redirectPath = searchParams.get('redirect');
          const target = redirectPath && isValidRedirect(redirectPath) ? redirectPath : '/';
          setIsProcessing(false);
          setTimeout(() => {
            const storedAccount = JSON.parse(localStorage.getItem('account') || '{}');
            if (getBaseDomain() && isMainDomain() && storedAccount.domain) {
              window.location.href = buildSubdomainUrl(storedAccount.domain, target);
            } else {
              navigate(target, { replace: true });
            }
          }, 500);
        } catch (err: any) {
          console.error('❌ Error processing tokens:', err);
          setError('Failed to process authentication data. Please try again.');
          setIsProcessing(false);
          setTimeout(() => navigate('/login'), 3000);
        }
        return;
      }

      // Original flow: code-based exchange (if backend sends code instead of tokens)
      if (!code && !accessToken) {
        console.error('❌ No authorization code or tokens received');
        setError('Invalid authentication response. Please try again.');
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        // Verify state (CSRF protection)
        const storedState = sessionStorage.getItem('oauth_state');
        const oauthFlow = sessionStorage.getItem('oauth_flow') || 'login';
        const oauthProvider = sessionStorage.getItem('oauth_provider') || 'google';

        if (storedState && storedState !== state) {
          console.error('❌ State mismatch - possible CSRF attack');
          setError('Security validation failed. Please try again.');
          setIsProcessing(false);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        console.log('✅ Authorization code received, exchanging for tokens...');
        console.log('📋 OAuth flow:', oauthFlow);
        console.log('📋 OAuth provider:', oauthProvider);

        // Determine which callback endpoint to use based on flow and provider
        let callbackEndpoint: string;
        if (oauthProvider === 'microsoft') {
          callbackEndpoint = oauthFlow === 'register'
            ? '/account/microsoft-register-callback'
            : '/account/microsoft-login-callback';
        } else {
          // Default to Google
          callbackEndpoint = oauthFlow === 'register'
            ? '/account/google-register-callback'
            : '/account/google-login-callback';
        }

        // Call backend callback endpoint (backend already handled the redirect)
        // The URL params were passed by Keycloak
        const baseUrl = import.meta.env.VITE_PUBLIC_API_BASE_URL;
        const fullUrl = `${baseUrl}${callbackEndpoint}?code=${code}&state=${state}`;
        console.log('🚀 Calling backend:', fullUrl);

        const response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        });

        const data = await response.json();
        console.log('📦 Backend response:', data);

        // Check for backend errors (like account_not_registered)
        if (!response.ok || !data.success) {
          if (data.error === 'account_not_registered') {
            setError('No account found. Please sign up first.');
            setIsProcessing(false);
            setTimeout(() => navigate('/register'), 3000);
            return;
          }

          throw new Error(data.error || 'Failed to complete authentication');
        }

        console.log('✅ Received tokens from backend');

        // Save auth data using context
        setAuthData(data);

        // Clear OAuth state
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_flow');
        sessionStorage.removeItem('oauth_provider');

        console.log('✅ Authentication successful!');

        // Redirect to intended path or dashboard, with subdomain awareness
        const redirectPath = searchParams.get('redirect');
        const target = redirectPath && isValidRedirect(redirectPath) ? redirectPath : '/';
        setIsProcessing(false);
        setTimeout(() => {
          const storedAccount = JSON.parse(localStorage.getItem('account') || '{}');
          if (getBaseDomain() && isMainDomain() && storedAccount.domain) {
            window.location.href = buildSubdomainUrl(storedAccount.domain, target);
          } else {
            navigate(target, { replace: true });
          }
        }, 500);
      } catch (err: any) {
        console.error('❌ Error during OAuth callback:', err);
        setError(err.message || 'Failed to complete login. Please try again.');
        setIsProcessing(false);
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setAuthData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-violet-100">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        {error ? (
          <>
            <div className="text-center mb-4">
              <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
              Authentication Failed
            </h2>
            <p className="text-center text-gray-600 mb-4">{error}</p>
            <p className="text-center text-sm text-gray-500">
              Redirecting...
            </p>
          </>
        ) : isProcessing ? (
          <>
            <div className="text-center mb-4">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
            <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
              Completing Sign In
            </h2>
            <p className="text-center text-gray-600">
              Please wait while we set up your account...
            </p>
          </>
        ) : (
          <>
            <div className="text-center mb-4">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-center text-gray-900 mb-2">
              Success!
            </h2>
            <p className="text-center text-gray-600">
              Redirecting to dashboard...
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
